import { faceMark } from './face-mark.js';
import { icon } from './icons.js';
import type { Section } from '../router.js';

export type IconName = 'chat' | 'profile' | 'shield' | 'memory' | 'connectors' | 'models' | 'devices' | 'wifi' | 'activity' | 'settings' | 'home' | 'sparkle';

const ICON_NAMES: Record<string, IconName> = {
  chat: 'sparkle',
  today: 'home',
  scan: 'activity',
  personas: 'profile',
  grupos: 'shield',
  conocimiento: 'memory',
  connectors: 'connectors',
  models: 'models',
  devices: 'devices',
  wifi: 'wifi',
  memory: 'memory',
  activity: 'activity',
  profile: 'profile',
  settings: 'settings',
};

type Item = { id: Section; label: string; icon: keyof typeof ICON_NAMES; group: 'principal' | 'espacio' | 'sistema' };

const ITEMS: Item[] = [
  { id: 'conversar', label: 'ASIS', icon: 'chat', group: 'principal' },
  { id: 'hoy', label: 'Hoy', icon: 'today', group: 'principal' },
  { id: 'escaneo', label: 'Escaneo', icon: 'scan', group: 'principal' },
  { id: 'personas', label: 'Personas', icon: 'personas', group: 'espacio' },
  { id: 'grupos', label: 'Grupos', icon: 'grupos', group: 'espacio' },
  { id: 'conocimiento', label: 'Conocimiento', icon: 'conocimiento', group: 'espacio' },
  { id: 'conectores', label: 'Conectores', icon: 'connectors', group: 'espacio' },
  { id: 'modelos', label: 'Modelos', icon: 'models', group: 'espacio' },
  { id: 'dispositivos', label: 'Dispositivos', icon: 'devices', group: 'sistema' },
  { id: 'redes', label: 'Redes Wi-Fi', icon: 'wifi', group: 'sistema' },
  { id: 'memoria', label: 'Memoria', icon: 'memory', group: 'sistema' },
  { id: 'actividad', label: 'Actividad', icon: 'activity', group: 'sistema' },
  { id: 'perfil', label: 'Mi cuenta', icon: 'profile', group: 'sistema' },
  { id: 'ajustes', label: 'Ajustes', icon: 'settings', group: 'sistema' },
];

const PRIMARY_IDS: Section[] = ['hoy', 'conversar', 'dispositivos', 'conectores', 'memoria'];
const PRIMARY_MOBILE_IDS: Section[] = ['hoy', 'conversar', 'dispositivos', 'conectores'];
const MORE_MOBILE_IDS: Section[] = ['escaneo', 'memoria', 'personas', 'conocimiento', 'actividad', 'ajustes', 'perfil', 'redes', 'modelos', 'grupos'];

export interface SidebarAccount {
  displayName: string;
  username: string;
  email?: string | null;
  onLogout: () => void;
}

function createSpaceItem(current: Section, onSelect: (next: Section) => void, mobile = false): HTMLLIElement {
  const li = document.createElement('li');
  const link = document.createElement('a');
  link.className = mobile ? 'nav-item nav-more-item' : 'nav-item';
  link.setAttribute('aria-label', 'Espacio');
  link.title = 'Espacio';
  link.href = '#/espacio';
  link.setAttribute('aria-current', current === 'espacio' ? 'page' : 'false');
  link.innerHTML = `${icon('plus', { width: mobile ? '20' : '18', height: mobile ? '20' : '18' })}<span>Espacio</span>`;
  link.addEventListener('click', event => { event.preventDefault(); onSelect('espacio'); });
  li.appendChild(link);
  return li;
}

export function renderSidebar(current: Section, onSelect: (next: Section) => void, account?: SidebarAccount): HTMLElement {
  const nav = document.createElement('nav');
  nav.className = current === 'espacio' ? 'sidebar sidebar-shell sidebar-rail' : 'sidebar sidebar-shell';
  nav.setAttribute('aria-label', 'Secciones');

  const brand = document.createElement('div');
  brand.className = 'brand';
  brand.innerHTML = `${faceMark()}<span class="brand-text">ASIS</span>`;

  const desktopList = document.createElement('ul');
  desktopList.className = 'nav-list nav-list-desktop';
  for (const id of PRIMARY_IDS) {
    const s = ITEMS.find(item => item.id === id)!;
    const li = document.createElement('li');
    const a = document.createElement('a');
    a.className = 'nav-item';
    a.setAttribute('aria-label', s.label);
    a.title = s.label;
    a.href = `#/${s.id}`;
    a.setAttribute('aria-current', current === s.id ? 'page' : 'false');
    a.innerHTML = `${icon(ICON_NAMES[s.icon]!)}<span>${s.label}</span>`;
    a.addEventListener('click', event => { event.preventDefault(); onSelect(s.id); });
    li.append(a); desktopList.append(li);
    if (id === 'conversar') desktopList.append(createSpaceItem(current, onSelect));
  }

  const primaryList = document.createElement('ul');
  primaryList.className = 'nav-list nav-list-mobile-primary';
  for (const id of PRIMARY_MOBILE_IDS) {
    const s = ITEMS.find(i => i.id === id)!;
    const li = document.createElement('li');
    const a = document.createElement('a');
    a.className = 'nav-item';
    a.setAttribute('aria-label', s.label);
    a.href = `#/${s.id}`;
    a.setAttribute('aria-current', current === s.id ? 'page' : 'false');
    a.innerHTML = `${icon(ICON_NAMES[s.icon]!, { width: '20', height: '20' })}<span>${s.label}</span>`;
    a.addEventListener('click', (e) => {
      e.preventDefault();
      onSelect(s.id);
    });
    li.appendChild(a);
    primaryList.appendChild(li);
  }

  const moreWrapper = document.createElement('div');
  moreWrapper.className = 'nav-more';
  const moreButton = document.createElement('button');
  moreButton.type = 'button';
  moreButton.className = 'nav-item nav-more-trigger';
  moreButton.setAttribute('aria-current', MORE_MOBILE_IDS.includes(current) && !(current === 'memoria' && window.matchMedia?.('(min-width: 1024px)').matches) ? 'page' : 'false');
  moreButton.title = 'Más';
  moreButton.setAttribute('aria-controls', 'sidebar-more-panel');
  moreButton.setAttribute('aria-expanded', 'false');
  moreButton.innerHTML = `${icon('more', { width: '20', height: '20' })}<span>Más</span>`;

  const morePanel = document.createElement('div');
  morePanel.className = 'nav-more-panel';
  morePanel.id = 'sidebar-more-panel';
  morePanel.setAttribute('aria-label', 'Más secciones');
  morePanel.hidden = true;

  const moreList = document.createElement('ul');
  moreList.className = 'nav-more-list';
  for (const id of MORE_MOBILE_IDS) {
    const s = ITEMS.find(i => i.id === id)!;
    const li = document.createElement('li');
    const a = document.createElement('a');
    a.className = 'nav-item nav-more-item';
    a.setAttribute('aria-label', s.label);
    a.href = `#/${s.id}`;
    a.setAttribute('aria-current', current === s.id ? 'page' : 'false');
    a.innerHTML = `${icon(ICON_NAMES[s.icon]!, { width: '20', height: '20' })}<span>${s.label}</span>`;
    a.addEventListener('click', (e) => {
      e.preventDefault();
      onSelect(s.id);
      closeMore();
      moreButton.focus();
    });
    if (s.id === 'memoria') li.className = 'nav-more-mobile-only';
    li.appendChild(a);
    moreList.appendChild(li);
    if (s.id === 'modelos') moreList.appendChild(createSpaceItem(current, onSelect, true));
  }

  if (account) {
    const accSep = document.createElement('li');
    accSep.className = 'nav-more-sep';
    accSep.setAttribute('role', 'separator');
    moreList.appendChild(accSep);

    const accountRow = document.createElement('li');
    accountRow.className = 'nav-more-account';
    const avatar = document.createElement('div');
    avatar.className = 'avatar';
    avatar.setAttribute('aria-hidden', 'true');
    avatar.textContent = account.displayName.trim().charAt(0).toUpperCase() || '?';
    const meta = document.createElement('div');
    meta.className = 'account-meta';
    const name = document.createElement('span');
    name.className = 'account-name';
    name.textContent = account.displayName;
    const handle = document.createElement('span');
    handle.className = 'account-handle';
    handle.textContent = account.email ?? `@${account.username}`;
    meta.append(name, handle);
    accountRow.append(avatar, meta);
    moreList.appendChild(accountRow);

    const logoutItem = document.createElement('li');
    const logout = document.createElement('button');
    logout.type = 'button';
    logout.className = 'btn ghost small nav-more-logout';
    logout.innerHTML = `${icon('power', { width: '14', height: '14' })}<span>Cerrar sesión</span>`;
    logout.addEventListener('click', () => {
      account.onLogout();
      closeMore();
    });
    logoutItem.appendChild(logout);
    moreList.appendChild(logoutItem);
  }
  morePanel.appendChild(moreList);
  moreWrapper.append(moreButton, morePanel);

  function closeMore(): void {
    morePanel.hidden = true;
    moreButton.setAttribute('aria-expanded', 'false');
    moreButton.classList.remove('open');
  }

  moreButton.addEventListener('click', () => {
    const open = morePanel.hidden;
    if (open) {
      morePanel.hidden = false;
      moreButton.setAttribute('aria-expanded', 'true');
      moreButton.classList.add('open');
      const desktop = window.matchMedia?.('(min-width: 1024px)').matches;
      const first = morePanel.querySelector<HTMLElement>(desktop ? 'li:not(.nav-more-mobile-only) > .nav-more-item' : '.nav-more-item');
      first?.focus();
    } else {
      closeMore();
    }
  });

  const onKeydown = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape') return;
    if (morePanel.hidden) return;
    e.preventDefault();
    closeMore();
    moreButton.focus();
  };
  morePanel.addEventListener('keydown', onKeydown);

  nav.append(brand, desktopList, primaryList, moreWrapper);

  const footer = document.createElement('div');
  footer.className = 'sidebar-footer';
  if (account) {
    const wrap = document.createElement('div');
    wrap.className = 'account account-footer';
    const avatar = document.createElement('div');
    avatar.className = 'avatar';
    avatar.setAttribute('aria-hidden', 'true');
    avatar.textContent = account.displayName.trim().charAt(0).toUpperCase() || '?';
    const meta = document.createElement('div');
    meta.className = 'account-meta';
    const name = document.createElement('span');
    name.className = 'account-name';
    name.textContent = account.displayName;
    const handle = document.createElement('span');
    handle.className = 'account-handle';
    handle.textContent = account.email ?? `@${account.username}`;
    meta.append(name, handle);
    const logout = document.createElement('button');
    logout.type = 'button';
    logout.className = 'btn ghost small';
    logout.innerHTML = `${icon('power', { width: '14', height: '14' })}<span>Cerrar sesión</span>`;
    logout.addEventListener('click', () => { account.onLogout(); });
    const profileLink = document.createElement('a');
    profileLink.className = 'account-link';
    profileLink.href = '#/perfil';
    profileLink.setAttribute('aria-label', 'Abrir mi cuenta');
    profileLink.append(avatar, meta);
    profileLink.addEventListener('click', event => { event.preventDefault(); onSelect('perfil'); });
    wrap.append(profileLink, logout);
    footer.append(wrap);
  } else {
    const text = document.createElement('span');
    text.className = 'footer-text';
    text.textContent = 'Tu espacio con ASIS';
    footer.append(text);
  }

  brand.after(footer);
  return nav;
}
